import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

export interface VendorPortalAuthResponse {
  accessToken: string;
  supplierId: number;
  organizationId: number;
  email: string;
}

export interface VendorPortalDashboard {
  supplierId: number;
  supplierName: string;
  openRfqs: number;
  openPos: number;
  pendingInvoices: number;
}

export interface VendorRfqSummary {
  id: number;
  uuid: string;
  rfqNumber: string;
  rfqStatus: string;
  deadlineAt?: string;
  responseStatus: string;
}

export interface VendorPoSummary {
  id: number;
  uuid: string;
  poNumber: string;
  poStatus: string;
  totalAmount: number;
  expectedDeliveryDate?: string;
}

export interface VendorBillSummary {
  id: number;
  uuid: string;
  billNumber: string;
  billStatus: string;
  totalAmount: number;
  paidAmount: number;
  dueDate?: string;
}

const vendorClient = api;

export function setVendorPortalToken(token: string | null) {
  if (token) {
    vendorClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete vendorClient.defaults.headers.common.Authorization;
  }
}

export const vendorPortalApi = {
  requestLink: (organizationId: number, email: string) =>
    unwrap(api.post<ApiResponse<{ status: string }>>('/v1/vendor-portal/auth/request-link', { organizationId, email })),

  verify: (token: string) =>
    unwrap(api.post<ApiResponse<VendorPortalAuthResponse>>('/v1/vendor-portal/auth/verify', { token })),

  dashboard: () =>
    unwrap(vendorClient.get<ApiResponse<VendorPortalDashboard>>('/v1/vendor-portal/procurement/dashboard')),

  listRfqs: () =>
    unwrap(vendorClient.get<ApiResponse<VendorRfqSummary[]>>('/v1/vendor-portal/procurement/rfqs')),

  listPurchaseOrders: () =>
    unwrap(vendorClient.get<ApiResponse<VendorPoSummary[]>>('/v1/vendor-portal/procurement/purchase-orders')),

  listInvoices: () =>
    unwrap(vendorClient.get<ApiResponse<VendorBillSummary[]>>('/v1/vendor-portal/procurement/invoices')),

  getRfq: (id: number) =>
    unwrap(vendorClient.get<ApiResponse<VendorRfqSummary>>(`/v1/vendor-portal/procurement/rfqs/${id}`)),

  submitQuotation: (body: {
    rfqId: number;
    deliveryDate?: string;
    paymentTerms?: string;
    warrantyTerms?: string;
    items: { productId: number; quantity: number; unitPrice: number; taxPercentage?: number }[];
  }) =>
    unwrap(vendorClient.post<ApiResponse<{ id: number; quotationNumber: string }>>('/v1/vendor-portal/procurement/quotations', body)),

  submitInvoice: (body: {
    purchaseOrderId: number;
    supplierInvoiceNumber: string;
    billDate?: string;
    dueDate?: string;
    items: { productId: number; quantity: number; unitPrice: number; taxPercentage?: number }[];
  }) =>
    unwrap(vendorClient.post<ApiResponse<{ id: number; billNumber: string }>>('/v1/vendor-portal/procurement/invoices', body)),
};
